import { MouseEvent, ReactNode } from 'react';
import styles from './ToastBanner.module.css';

export interface ToastBannerProps {
  /** Leading icon, already carrying its own data-name. */
  icon: ReactNode;
  /** Label of the action button (e.g. "View", "Accept"). */
  buttonText: string;
  /**
   * Runs when the banner body or the action button is clicked. Fires exactly
   * once per click: the button's handler stops propagation, so its clicks
   * never reach the container's handler.
   */
  onAction: () => void;
  /**
   * Whether clicking the banner body runs `onAction`. Set false where the
   * action is destructive or irreversible enough that an unlabelled
   * full-width click target is the wrong affordance, leaving the button as
   * the only trigger. Defaults to true.
   */
  onActionOnClick?: boolean;
  /** data-name of the banner container. */
  dataName: string;
  /** data-name of the action button. */
  buttonDataName: string;
  /** Text content between the icon and the button. */
  children: ReactNode;
}

/**
 * Single-line toast banner layout shared by the conversation toasts (TT911
 * and interagency chat): icon, text content, action button. The button is the
 * keyboard-accessible path; the container click is a pointer-only convenience
 * with the same action, which `onActionOnClick` turns off. The close button
 * react-toastify renders outside this component dismisses without triggering
 * the action.
 */
function ToastBanner({
  icon,
  buttonText,
  onAction,
  onActionOnClick = true,
  dataName,
  buttonDataName,
  children,
}: ToastBannerProps) {
  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Without this the click bubbles to the container's handler and the
    // action fires twice.
    event.stopPropagation();
    onAction();
  };

  return (
    <div
      className={
        onActionOnClick
          ? `${styles.container} ${styles.clickable}`
          : styles.container
      }
      data-name={dataName}
      onClick={onActionOnClick ? onAction : undefined}
    >
      <span className={styles.icon}>{icon}</span>
      <div className={styles.content}>{children}</div>
      <button
        type="button"
        className={styles.actionButton}
        data-name={buttonDataName}
        onClick={handleButtonClick}
      >
        {buttonText}
      </button>
    </div>
  );
}

export default ToastBanner;
